/* VINOD search suggestions — products-only predictive dropdown for the header search.

   v2: self-contained. The panel is appended to <body> and positioned against the input's
   own bounding box, and all styles are injected by this file. That removes two whole
   classes of failure seen on the live theme: a missing/blocked vinod-search.css, and the
   theme's pill-shaped search form stretching or clipping a panel nested inside it.
   vinod-search.css is no longer required.

   Attaches itself to whatever search input the theme renders (no markup changes needed):
   any input[name="q"] inside the header or search drawer.

   WHY A CUSTOM VIEW, NOT /search/suggest.json: the predictive endpoint matches titles,
   vendor and type only — products that match a TAG never appear. VINOD tags products with
   multi-language and regional terms so those words find the product, and the storefront
   search index does cover tags. So the primary source is the JSON search view
   (templates/search.vd-suggest.liquid); the predictive endpoint is only a fallback for when
   that view has not been installed. */
(function () {
  var VIEW = '/search?type=product&options[prefix]=last&view=vd-suggest';
  var FALLBACK = '/search/suggest.json?resources[type]=product&resources[limit]=8' +
    '&resources[options][unavailable_products]=last';
  var LIMIT = 8;
  var DEBOUNCE = 90;
  var RECENT_KEY = 'vd:recent-searches';
  var useFallback = false;

  /* Every resolved query is kept for the life of the page. Shoppers type, backspace and
     retype constantly, so most keystrokes after the first few can be answered with no
     network round-trip at all. */
  var CACHE = {};

  /* Bestsellers for the zero-result fallback. Fetched once per visit and kept in
     sessionStorage — the list changes daily at most, and a shopper who hits two dead ends
     should not pay for it twice. */
  var BEST_KEY = 'vd:best';
  var BEST_URL = '/collections/all?sort_by=best-selling&view=vd-suggest';
  var bestCache = null;

  /* Filler words a shopper types in a natural-language query ("dinner set FOR 4 PEOPLE").
     They carry no product meaning, and including them in an AND query throws away every
     real match, so they are dropped before the query is built. */
  var STOP = ('a an and are as at by for from in is it me my of on or per the to with' +
    ' set-of pcs pc piece pieces people person adults kg litre liter ltr inch cm').split(' ');

  var POPULAR = [
    { label: 'Masala Dabba', q: 'masala dabba' },
    { label: 'Pressure Cooker', q: 'pressure cooker' },
    { label: 'Tiffin', q: 'tiffin' },
    { label: 'Kadhai', q: 'kadhai' },
    { label: 'Dinner Set', q: 'dinner set' },
    { label: 'Thali', q: 'thali' }
  ];

  /* ── styles ─────────────────────────────────────────────────────────────────
     Injected here so the dropdown never depends on a separate asset. !important on
     the layout-critical properties only, so a theme rule cannot flatten the rows. */
  /* ── insights logging ───────────────────────────────────────────────────────────
     Records what shoppers search for, so the search-insights dashboard has data. Three
     rules this follows, because it runs on every keystroke of a live storefront:

     1. It never delays or blocks the dropdown. Events are queued and posted separately,
        and a failed post is swallowed.
     2. It logs the SETTLED term, not every keystroke. Typing "dabba" would otherwise
        record d/da/dab/dabb/dabba as five searches, wrecking the report and burning
        database writes. A term is logged 1.2s after the shopper stops typing.
     3. It stores nothing identifying. The session id is a random string in
        sessionStorage, gone when the tab closes, and resolves to no one. */
  var LOG_URL = 'https://vinod-search-insights.web-a2a.workers.dev/e';
  var SID_KEY = 'vd:sid';
  var SETTLE = 1200;
  var logQueue = [];
  var flushTimer, settleTimer, lastLogged = '';

  function sid() {
    try {
      var s = sessionStorage.getItem(SID_KEY);
      if (!s) {
        s = 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(SID_KEY, s);
      }
      return s;
    } catch (e) { return ''; }   /* private mode, or storage disabled — log nothing */
  }

  function pageName() {
    var m = /template-([a-z0-9_-]+)/.exec((document.body && document.body.className) || '');
    return m ? m[1].slice(0, 40) : 'unknown';
  }

  /* sendBeacon on unload, so a search followed immediately by a click on a result is not
     lost when the page navigates away.

     BOTH paths send text/plain, not application/json. An application/json body is not a
     CORS-simple request, so the browser demands a preflight — and sendBeacon cannot
     preflight, so the browser drops the beacon silently, with no console error. That is
     what lost every click event in the first day of logging. text/plain is simple, needs
     no preflight, and the Worker reads the body as text before parsing it anyway. */
  var LOG_TYPE = 'text/plain;charset=UTF-8';

  function flush(unloading) {
    if (!logQueue.length) return;
    var body = JSON.stringify({ events: logQueue.splice(0, 20) });
    try {
      if (unloading && navigator.sendBeacon) {
        navigator.sendBeacon(LOG_URL, new Blob([body], { type: LOG_TYPE }));
        return;
      }
      fetch(LOG_URL, {
        method: 'POST',
        headers: { 'Content-Type': LOG_TYPE },
        body: body,
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  function log(kind, data) {
    var s = sid();
    if (!s) return;
    var e = { kind: kind, s: s, page: pageName() };
    if (data) for (var k in data) e[k] = data[k];
    logQueue.push(e);
    clearTimeout(flushTimer);
    flushTimer = setTimeout(function () { flush(false); }, 900);
  }

  function logSettled(term, n) {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      if (term === lastLogged) return;
      lastLogged = term;
      log(n ? 'search' : 'zero', { term: term, n: n });
    }, SETTLE);
  }

  /* A click or submit means the term mattered — log it now rather than waiting out the
     settle timer, because the page is about to navigate. */
  function logSettledNow(term, n) {
    clearTimeout(settleTimer);
    if (term && term !== lastLogged) {
      lastLogged = term;
      log(n ? 'search' : 'zero', { term: term, n: n });
    }
  }

  /* Product handle from the result URL. The search view returns no product id, and the
     handle is stable, readable in the report, and enough to identify the product. */
  function handleOf(href) {
    var m = /\/products\/([^/?#]+)/.exec(String(href || ''));
    return m ? decodeURIComponent(m[1]).slice(0, 40) : null;
  }

  window.addEventListener('pagehide', function () { flush(true); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush(true);
  });

  var CSS = [
    '.vd-sg{position:fixed!important;z-index:2147483000!important;box-sizing:border-box;',
    'max-height:min(70vh,520px);overflow-y:auto;overscroll-behavior:contain;',
    'background:#fff!important;border:1px solid #e2e5ec!important;border-radius:12px!important;',
    'box-shadow:0 16px 40px rgba(49,54,82,.16)!important;',
    'font-family:Mulish,system-ui,sans-serif;text-align:left!important;margin:0!important;padding:0!important;',
    'animation:vd-sg-in .16s cubic-bezier(.16,1,.3,1)}',
    '.vd-sg[hidden]{display:none!important}',
    '@keyframes vd-sg-in{from{opacity:0;transform:translateY(-4px)}}',
    '.vd-sg *{box-sizing:border-box}',
    '.vd-sg__head{padding:13px 15px 6px!important;font-family:Poppins,system-ui,sans-serif;',
    'font-size:10.5px!important;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#8b93a5!important;margin:0!important}',
    '.vd-sg__chips{display:flex!important;flex-wrap:wrap!important;gap:8px!important;padding:8px 15px 14px!important;margin:0!important}',
    '.vd-sg__chip{display:inline-flex!important;align-items:center;height:30px;padding:0 13px!important;',
    'background:#f2f3f6!important;border:1px solid #e6e9ef!important;border-radius:999px!important;',
    'font-size:12.5px!important;line-height:1!important;color:#313652!important;text-decoration:none!important;white-space:nowrap;',
    'transition:background .16s,color .16s,border-color .16s}',
    '.vd-sg__chip:hover,.vd-sg__chip.is-active{background:#313652!important;border-color:#313652!important;color:#fff!important}',
    '.vd-sg__row{display:flex!important;align-items:center!important;gap:12px!important;padding:10px 15px!important;',
    'margin:0!important;color:#262b45!important;text-decoration:none!important;transition:background .14s}',
    '.vd-sg__row:hover,.vd-sg__row.is-active{background:#f7f8fb!important}',
    '.vd-sg__row img,.vd-sg__ph{flex:none!important;width:46px!important;height:46px!important;',
    'object-fit:contain!important;background:#f8f9fb!important;border:1px solid #eceef2!important;border-radius:9px!important;margin:0!important;display:block!important}',
    '.vd-sg__txt{min-width:0!important;display:flex!important;flex-direction:column!important;gap:3px!important}',
    '.vd-sg__txt>b{font-size:13px!important;font-weight:600!important;line-height:1.32!important;color:#262b45!important;',
    'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
    '.vd-sg__txt>b b{font-weight:800!important;color:#ce2a26!important}',
    '.vd-sg__tag{align-self:flex-start;padding:1px 8px!important;background:#fdeceb!important;border-radius:999px!important;',
    'font-size:10.5px!important;font-weight:600!important;color:#ce2a26!important;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '.vd-sg__price{display:flex!important;align-items:baseline!important;gap:7px!important;',
    'font-family:Poppins,system-ui,sans-serif;font-size:12.5px!important;font-weight:700!important;color:#313652!important}',
    '.vd-sg__price s{font-weight:500!important;font-size:11.5px!important;color:#9aa2b2!important;text-decoration:line-through}',
    '.vd-sg__price em{font-style:normal;font-size:11px!important;font-weight:600!important;color:#9aa2b2!important}',
    '.vd-sg__all{display:block!important;padding:13px 15px!important;border-top:1px solid #eceef2!important;',
    'font-family:Poppins,system-ui,sans-serif;font-size:12.5px!important;font-weight:700!important;',
    'color:#313652!important;text-decoration:none!important;text-align:center!important;transition:background .14s,color .14s}',
    '.vd-sg__all:hover,.vd-sg__all.is-active{background:#313652!important;color:#fff!important}',
    '.vd-sg__empty{padding:18px 15px!important;font-size:13px!important;color:#5b6274!important}',
    '.vd-sg__empty span{display:block;margin-top:6px;font-size:12.5px;color:#8b93a5}',
    '.vd-sg__note{padding:15px 15px 8px!important;margin:0!important}',
    '.vd-sg__note b{display:block!important;font-family:Poppins,system-ui,sans-serif;font-size:13.5px!important;',
    'font-weight:700!important;color:#262b45!important;margin:0 0 4px!important}',
    '.vd-sg__note em{display:block!important;font-style:normal;font-size:12.5px!important;color:#8b93a5!important}',
    '.vd-empty{max-width:1180px;margin:0 auto;padding:8px 24px 56px;font-family:Mulish,system-ui,sans-serif}',
    '.vd-empty h2{font-family:Poppins,system-ui,sans-serif;font-size:20px;font-weight:800;color:#313652;margin:0 0 6px}',
    '.vd-empty p{font-size:14.5px;line-height:1.7;color:#5b6274;margin:0 0 22px;max-width:60ch}',
    '.vd-empty__grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:16px}',
    '.vd-empty__card{display:flex;flex-direction:column;gap:10px;padding:14px;background:#fff;',
    'border:1px solid #e2e5ec;border-radius:13px;color:#262b45;text-decoration:none;transition:box-shadow .18s}',
    '.vd-empty__card:hover{box-shadow:0 10px 26px rgba(49,54,82,.12)}',
    '.vd-empty__card img{width:100%;aspect-ratio:1;object-fit:contain;background:#f8f9fb;border-radius:9px;display:block}',
    '.vd-empty__card b{font-size:13px;font-weight:600;line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;',
    '-webkit-box-orient:vertical;overflow:hidden}',
    '.vd-empty__card span{font-family:Poppins,system-ui,sans-serif;font-size:13px;font-weight:700;color:#313652}',
    '.vd-sg.is-loading{opacity:1}',
    '.vd-sg__sk{display:flex!important;align-items:center!important;gap:12px!important;padding:10px 15px!important}',
    '.vd-sg__sk i{flex:none;width:46px;height:46px;border-radius:9px}',
    '.vd-sg__sk span{display:flex!important;flex-direction:column!important;gap:6px!important;flex:1;min-width:0}',
    '.vd-sg__sk b,.vd-sg__sk em{display:block;height:10px;border-radius:5px}',
    '.vd-sg__sk b{width:78%}.vd-sg__sk em{width:34%;height:9px}',
    '.vd-sg__sk i,.vd-sg__sk b,.vd-sg__sk em{background:#eef0f4;',
    'background-image:linear-gradient(90deg,#eef0f4 0%,#f7f8fb 50%,#eef0f4 100%);background-size:200% 100%;',
    'animation:vd-sg-sh 1.1s linear infinite}',
    '@keyframes vd-sg-sh{from{background-position:200% 0}to{background-position:-200% 0}}',
    '@media (max-width:768px){.vd-sg{max-height:62vh;border-radius:10px!important}.vd-sg__row{padding:9px 12px!important}}'
  ].join('');

  function injectCss() {
    if (document.getElementById('vd-sg-css')) return;
    var s = document.createElement('style');
    s.id = 'vd-sg-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function money(cents) {
    return '\u20b9' + Math.round(cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function mark(text, q) {
    var t = esc(text);
    if (!q) return t;
    var i = t.toLocaleLowerCase().indexOf(q.toLocaleLowerCase());
    if (i < 0) return t;
    return t.slice(0, i) + '<b>' + t.slice(i, i + q.length) + '</b>' + t.slice(i + q.length);
  }
  function matchedTag(tags, q) {
    if (!tags || !tags.length) return '';
    var needle = q.toLocaleLowerCase();
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toLocaleLowerCase().indexOf(needle) > -1) return tags[i];
    }
    return '';
  }

  function recent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, 5); }
    catch (e) { return []; }
  }
  function remember(term) {
    if (!term) return;
    try {
      var list = recent().filter(function (t) { return t.toLocaleLowerCase() !== term.toLocaleLowerCase(); });
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
    } catch (e) {}
  }

  function attach(input) {
    if (input.__vdSuggest) return;
    if (!input.offsetParent && input.type === 'hidden') return;
    input.__vdSuggest = true;

    var form = input.closest('form');
    var wrap = document.createElement('div');
    wrap.className = 'vd-sg';
    wrap.setAttribute('hidden', '');
    wrap.setAttribute('role', 'listbox');
    document.body.appendChild(wrap);

    input.setAttribute('autocomplete', 'off');
    var timer, controller, activeIndex = -1, lastQuery = '';
    var lastRendered = { term: '', n: 0 };

    /* anchor to the input (or its form, whichever is wider) rather than nesting inside it */
    function place() {
      var base = form && form.getBoundingClientRect().width > 260 ? form : input;
      var r = base.getBoundingClientRect();
      var w = Math.max(300, Math.min(r.width, window.innerWidth - 24));
      var left = Math.min(Math.max(8, r.left), window.innerWidth - w - 8);
      wrap.style.top = Math.round(r.bottom + 8) + 'px';
      wrap.style.left = Math.round(left) + 'px';
      wrap.style.width = Math.round(w) + 'px';
    }

    function items() { return wrap.querySelectorAll('[data-sg-item]'); }
    function open() { place(); wrap.removeAttribute('hidden'); }
    function close() { wrap.setAttribute('hidden', ''); activeIndex = -1; }
    function isOpen() { return !wrap.hasAttribute('hidden'); }

    function highlight(i) {
      var list = items();
      if (!list.length) return;
      activeIndex = (i + list.length) % list.length;
      Array.prototype.forEach.call(list, function (el, n) { el.classList.toggle('is-active', n === activeIndex); });
      var el = list[activeIndex];
      if (el.offsetTop < wrap.scrollTop) wrap.scrollTop = el.offsetTop - 8;
      var over = el.offsetTop + el.offsetHeight - (wrap.scrollTop + wrap.clientHeight);
      if (over > 0) wrap.scrollTop += over + 8;
    }

    function renderIdle() {
      var rec = recent(), html = '';
      if (rec.length) {
        html += '<div class="vd-sg__head">Recent searches</div><div class="vd-sg__chips">';
        rec.forEach(function (t) {
          html += '<a class="vd-sg__chip" data-sg-item href="/search?q=' + encodeURIComponent(t) + '">' + esc(t) + '</a>';
        });
        html += '</div>';
      }
      html += '<div class="vd-sg__head">Popular searches</div><div class="vd-sg__chips">';
      POPULAR.forEach(function (p) {
        html += '<a class="vd-sg__chip" data-sg-item href="/search?q=' + encodeURIComponent(p.q) + '">' + esc(p.label) + '</a>';
      });
      html += '</div>';
      wrap.innerHTML = html;
      open();
    }

    function renderProducts(list, q) {
      lastRendered = { term: q, n: list.length };
      if (!list.length) {
        renderEmpty(q);
        return;
      }
      var html = '';
      lastRendered = { term: q, n: list.length };
      list.slice(0, LIMIT).forEach(function (p, i) {
        var onSale = p.compare_at && p.compare_at > p.price;
        var titleHtml = mark(p.title, q);
        var tag = titleHtml.indexOf('<b>') < 0 ? matchedTag(p.tags, q) : '';
        html += '<a class="vd-sg__row" data-sg-item data-pos="' + (i + 1) + '" href="' + esc(p.url) + '">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" width="46" height="46" loading="lazy">'
                   : '<span class="vd-sg__ph"></span>') +
          '<span class="vd-sg__txt"><b>' + titleHtml + '</b>' +
          (tag ? '<span class="vd-sg__tag">' + esc(tag) + '</span>' : '') +
          '<span class="vd-sg__price">' + money(p.price) +
          (onSale ? '<s>' + money(p.compare_at) + '</s>' : '') +
          (p.available === false ? '<em>Sold out</em>' : '') +
          '</span></span></a>';
      });
      html += '<a class="vd-sg__all" data-sg-item href="/search?q=' + encodeURIComponent(q) +
        '&type=product&options[prefix]=last">See all products for \u201c' + esc(q) + '\u201d</a>';
      wrap.innerHTML = html;
      activeIndex = -1;
      open();
    }

    /* Painted the moment a new term is typed, so the popular-search chips are replaced on
       that keystroke instead of lingering until the response arrives. */
    function renderSkeleton() {
      var row = '<div class="vd-sg__sk"><i></i><span><b></b><em></em></span></div>';
      wrap.innerHTML = row + row + row + row;
      open();
    }

    /* ── zero-result fallback ─────────────────────────────────────────────────────
       A dead end is the worst thing search can do, so nothing is ever the final answer.
       Two attempts, in order:

       1. A near match, by shortening the longest word. "kadhaii" finds kadhai, "squate"
          finds square. The floor is 4 characters AND 60% of the original length — without
          it, "paneer" would relax to "pan" and confidently suggest frying pans, which is
          how a helpful feature turns into a broken-looking one.
       2. Failing that, the current bestsellers, labelled as suggestions rather than
          matches so nobody mistakes them for results. */
    function relaxedQueries(q) {
      var w = terms(q);
      if (!w.length) return [];
      var longest = w.slice().sort(function (a, b) { return b.length - a.length; })[0];
      var floor = Math.max(4, Math.ceil(longest.length * 0.6));
      var out = [];
      for (var len = longest.length - 1; len >= floor; len--) {
        out.push(longest.slice(0, len));
      }
      return out;
    }

    function fetchBest() { return bestsellers(); }

    function rowsHtml(list, q) {
      var html = '';
      list.slice(0, LIMIT).forEach(function (p, i) {
        var onSale = p.compare_at && p.compare_at > p.price;
        html += '<a class="vd-sg__row" data-sg-item data-pos="' + (i + 1) + '" href="' + esc(p.url) + '">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" width="46" height="46" loading="lazy">'
                   : '<span class="vd-sg__ph"></span>') +
          '<span class="vd-sg__txt"><b>' + mark(p.title, q) + '</b>' +
          '<span class="vd-sg__price">' + money(p.price) +
          (onSale ? '<s>' + money(p.compare_at) + '</s>' : '') +
          (p.available === false ? '<em>Sold out</em>' : '') +
          '</span></span></a>';
      });
      return html;
    }

    function noteHtml(title, sub) {
      return '<div class="vd-sg__note"><b>' + title + '</b><em>' + sub + '</em></div>';
    }

    function renderEmpty(q) {
      /* Show the honest line immediately, then fill underneath it. The shopper gets an
         answer on this keystroke rather than after two more round-trips. */
      wrap.innerHTML = noteHtml(
        'Nothing matched \u201c' + esc(q) + '\u201d exactly',
        'Looking for something close\u2026'
      );
      open();

      var tries = relaxedQueries(q);
      var token = q;

      function attempt(i) {
        if (q !== lastQuery) return;                       /* shopper moved on */
        if (i >= tries.length) return showBest();
        var t = tries[i];
        fetch(VIEW + '&q=' + encodeURIComponent(t + '*'), { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (d) {
            if (token !== lastQuery) return;
            var list = rank(d.results || [], t);
            if (!list.length) return attempt(i + 1);
            wrap.innerHTML = noteHtml(
              'Nothing matched \u201c' + esc(q) + '\u201d exactly',
              'Showing the closest we have for \u201c' + esc(t) + '\u201d'
            ) + rowsHtml(list, t);
            lastRendered = { term: q, n: 0 };
            open();
          })
          .catch(function () { attempt(i + 1); });
      }

      function showBest() {
        fetchBest().then(function (list) {
          if (token !== lastQuery) return;
          if (!list.length) {
            wrap.innerHTML = noteHtml(
              'Nothing matched \u201c' + esc(q) + '\u201d exactly',
              'Try a shorter word, or browse Cookware, Tableware and Tiffins.'
            );
            open();
            return;
          }
          wrap.innerHTML = noteHtml(
            'We don\u2019t have \u201c' + esc(q) + '\u201d',
            'Popular with our customers right now'
          ) + rowsHtml(list, '');
          open();
        });
      }

      attempt(0);
    }

    /* While a request is in flight, filter the nearest cached prefix's results so the
       panel updates on the SAME keystroke instead of waiting for the network. These rows
       are a subset of what the server will return (the search index only ever narrows as
       the term grows), so the real response replaces them without anything jumping. */
    function optimistic(q) {
      for (var n = q.length - 1; n >= 2; n--) {
        var hit = CACHE[q.slice(0, n)];
        if (!hit) continue;
        var w = terms(q).map(function (t) { return t.toLocaleLowerCase(); });
        return hit.filter(function (p) {
          var hay = (p.title || '').toLocaleLowerCase() + ' ' + (p.tags || []).join(' ').toLocaleLowerCase();
          return w.every(function (t) { return hay.indexOf(t) > -1; });
        });
      }
      return null;
    }

    /* Shopify's storefront search is OR across words by default, so "dinner set for 4
       people" also returns anything matching just "set" or "4" — which is why tiffins with
       "4 Compartment" outranked the dinner sets. Joining the meaningful words with AND
       narrows it to products matching all of them. The trailing * on the last word keeps
       results appearing while the shopper is still typing it. */
    function terms(q) {
      var words = q.split(/\s+/).filter(Boolean);
      var kept = words.filter(function (w) {
        return STOP.indexOf(w.toLocaleLowerCase()) < 0;
      });
      return kept.length ? kept : words;
    }

    function buildQuery(q, strict) {
      var w = terms(q);
      var last = w.length - 1;
      var out = w.map(function (t, i) { return i === last ? t + '*' : t; });
      return strict ? out.join(' AND ') : out.join(' ');
    }

    /* An AND query can still come back in an unhelpful order, and the OR fallback almost
       always does, so rank on our side: a whole-phrase title hit wins, then the number of
       query words present in the title, then in the tags. Sort is stable, so Shopify's own
       relevance order survives as the tie-breaker. */
    function rank(list, q) {
      var w = terms(q).map(function (t) { return t.toLocaleLowerCase(); });
      var phrase = q.toLocaleLowerCase();
      return list.map(function (p, i) {
        var title = (p.title || '').toLocaleLowerCase();
        var tags = (p.tags || []).join(' ').toLocaleLowerCase();
        var s = 0;
        if (title.indexOf(phrase) > -1) s += 100;
        w.forEach(function (t) {
          if (title.indexOf(t) > -1) s += 10;
          else if (tags.indexOf(t) > -1) s += 3;
        });
        if (title.indexOf(w[0]) === 0) s += 5;
        return { p: p, s: s, i: i };
      }).filter(function (x) {
        /* Score 0 means the term appeared in neither the title nor any tag — Shopify
           matched it in the product DESCRIPTION. That is why "paneer" returned eight
           products: the descriptions mention making paneer, though no such product is
           sold. Description matches are dropped rather than demoted, because a shopper
           reading "paneer" above a kadhai concludes search is broken.

           The consequence is deliberate: anything the store wants findable by a word
           needs that word in the title or as a tag. And a term with no real match now
           records as a zero-result, which is what makes the no-results report honest. */
        return x.s > 0;
      }).sort(function (a, b) {
        return b.s - a.s || a.i - b.i;
      }).map(function (x) { return x.p; });
    }

    function query(q, loose) {
      if (controller) controller.abort();
      controller = new AbortController();
      var opts = { signal: controller.signal, headers: { Accept: 'application/json' } };
      var url = useFallback
        ? FALLBACK + '&q=' + encodeURIComponent(buildQuery(q, false))
        : VIEW + '&q=' + encodeURIComponent(buildQuery(q, !loose));

      fetch(url, opts)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var list;
          if (useFallback) {
            var found = (((data.resources || {}).results || {}).products) || [];
            list = found.map(function (p) {
              return {
                title: p.title, url: p.url, price: p.price,
                compare_at: p.compare_at_price_min, available: p.available,
                image: p.featured_image && p.featured_image.url, tags: []
              };
            });
          } else {
            list = data.results || [];
          }
          /* AND found nothing (a typo, or a word we kept that no product carries) —
             retry once as OR rather than telling the shopper there are no matches. */
          if (!list.length && !loose && !useFallback && terms(q).length > 1) {
            query(q, true);
            return;
          }
          list = rank(list, q);
          CACHE[q] = list;
          logSettled(q, list.length);
          /* A stale response still populates the cache above — it just must not paint. */
          if (q !== lastQuery) return;
          wrap.classList.remove('is-loading');
          renderProducts(list, q);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (!useFallback) { useFallback = true; query(q, loose); return; }
          wrap.classList.remove('is-loading');
        });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      lastQuery = q;
      clearTimeout(timer);
      clearTimeout(settleTimer);
      if (q.length < 2) { wrap.classList.remove('is-loading'); renderIdle(); return; }

      if (CACHE[q]) {                       // already seen this term — no network, no wait
        wrap.classList.remove('is-loading');
        renderProducts(CACHE[q], q);
        logSettled(q, CACHE[q].length);
        return;
      }

      /* Only paint a non-empty guess. An empty one would flash "No products match" for a
         term the server may well have results for. */
      var guess = optimistic(q);
      if (guess && guess.length) renderProducts(guess, q);
      else renderSkeleton();
      wrap.classList.add('is-loading');
      timer = setTimeout(function () { query(q); }, DEBOUNCE);
    });

    input.addEventListener('focus', function () {
      var q = input.value.trim();
      if (q.length < 2) renderIdle();
      else if (CACHE[q]) renderProducts(CACHE[q], q);
      else open();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(activeIndex - 1); }
      else if (e.key === 'Enter') {
        var el = items()[activeIndex];
        remember(input.value.trim());
        if (el) { e.preventDefault(); window.location.assign(el.getAttribute('href')); }
      } else if (e.key === 'Escape') { close(); input.blur(); }
    });

    if (form) form.addEventListener('submit', function () {
      var q = input.value.trim();
      remember(q);
      logSettledNow(q, lastRendered.n);
      log('submit', { term: q, n: lastRendered.n });
    });

    wrap.addEventListener('mousedown', function (e) {
      remember(input.value.trim());
      var row = e.target && e.target.closest && e.target.closest('.vd-sg__row');
      if (!row) return;
      logSettledNow(lastRendered.term, lastRendered.n);
      log('click', {
        term: lastRendered.term,
        n: lastRendered.n,
        pid: handleOf(row.getAttribute('href')),
        pos: parseInt(row.getAttribute('data-pos'), 10) || null
      });
      flush(true);
    });
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target) && e.target !== input) close();
    });
    window.addEventListener('resize', function () { if (isOpen()) place(); });
    window.addEventListener('scroll', function () { if (isOpen()) place(); }, true);
  }

  function bestsellers() {
    if (bestCache && bestCache.length) return Promise.resolve(bestCache);
    try {
      var s = sessionStorage.getItem(BEST_KEY);
      if (s) {
        var parsed = JSON.parse(s);
        /* Only trust a non-empty cache. Caching an empty list would let one failed fetch
           poison the whole visit, which is exactly what happened on first test. */
        if (parsed && parsed.length) { bestCache = parsed; return Promise.resolve(bestCache); }
      }
    } catch (e) {}

    function keep(list) {
      /* Sold-out products are dropped rather than shown greyed out. This list appears
         when a shopper has already hit a dead end; offering something they cannot buy
         makes the second suggestion as useless as the first. Both sources return more
         than four so there is headroom to still fill four. */
      var out = list.filter(function (p) { return p.available !== false; }).slice(0, 4);
      if (out.length) {
        bestCache = out;
        try { sessionStorage.setItem(BEST_KEY, JSON.stringify(out)); } catch (e) {}
      }
      return out;
    }

    /* NO Accept header here. With `Accept: application/json`, Shopify answers this
       collection URL with a 404 — the same URL returns the template correctly in a normal
       browser navigation. The search view is unaffected, which is why only the bestseller
       fetch failed. */
    return fetch(BEST_URL)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var list = keep((d && d.results) || []);
        if (list.length) return list;
        throw new Error('empty');
      })
      .catch(function () {
        /* Second source: Shopify's built-in product JSON. Always available and needs no
           template, so the fallback cannot be broken by a theme change. Order is the
           collection's own rather than best-selling, which is an acceptable trade for
           never dead-ending. */
        return fetch('/collections/all/products.json?limit=10')
          .then(function (r) { return r.json(); })
          .then(function (d) {
            return keep((d.products || []).map(function (p) {
              var v = (p.variants && p.variants[0]) || {};
              return {
                title: p.title,
                url: '/products/' + p.handle,
                image: (p.images && p.images[0] && p.images[0].src) || null,
                price: Math.round(parseFloat(v.price || 0) * 100),
                compare_at: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : null,
                available: v.available !== false,
                tags: []
              };
            }));
          })
          .catch(function () { return []; });
      });
  }

  /* ── empty search RESULTS PAGE ─────────────────────────────────────────────
     Same treatment as the dropdown, for shoppers who press Enter. The theme's own empty
     state is left in place above this — replacing it would mean editing the search
     template, and appending is both safer and honest about what happened.

     Emptiness is detected by counting product links in the main region rather than by
     looking for a theme-specific "no results" class, which would break the next time the
     theme updates. */
  function pageFallback() {
    if (!/template-search/.test((document.body && document.body.className) || '')) return;
    if (document.getElementById('vd-empty')) return;

    var main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;
    if (main.querySelectorAll('a[href*="/products/"]').length > 1) return;

    var q = '';
    try { q = new URLSearchParams(location.search).get('q') || ''; } catch (e) {}
    q = q.replace(/[*"']/g, '').trim();
    if (!q) return;

    var box = document.createElement('div');
    box.className = 'vd-empty';
    box.id = 'vd-empty';
    main.appendChild(box);

    function paint(head, sub, list) {
      var cards = list.map(function (p) {
        var onSale = p.compare_at && p.compare_at > p.price;
        return '<a class="vd-empty__card" href="' + esc(p.url) + '">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '<img alt="">') +
          '<b>' + esc(p.title) + '</b><span>' + money(p.price) +
          (onSale ? ' <s style="font-weight:500;color:#9aa2b2">' + money(p.compare_at) + '</s>' : '') +
          '</span></a>';
      }).join('');
      box.innerHTML = '<h2>' + head + '</h2><p>' + sub + '</p>' +
        (cards ? '<div class="vd-empty__grid">' + cards + '</div>' : '');
    }

    paint('Nothing matched \u201c' + esc(q) + '\u201d exactly', 'Looking for something close\u2026', []);

    var words = q.split(/\s+/).filter(Boolean);
    var longest = words.slice().sort(function (a, b) { return b.length - a.length; })[0] || '';
    var floor = Math.max(4, Math.ceil(longest.length * 0.6));
    var tries = [];
    for (var l = longest.length - 1; l >= floor; l--) tries.push(longest.slice(0, l));

    function hit(p, t) {
      var n = t.toLowerCase();
      return (p.title || '').toLowerCase().indexOf(n) > -1 ||
             (p.tags || []).join(' ').toLowerCase().indexOf(n) > -1;
    }

    function attempt(i) {
      if (i >= tries.length) {
        bestsellers().then(function (list) {
          if (!list.length) {
            paint('Nothing matched \u201c' + esc(q) + '\u201d',
              'Try a shorter word, or browse Cookware, Tableware and Tiffins.', []);
            return;
          }
          paint('We don\u2019t have \u201c' + esc(q) + '\u201d',
            'Popular with our customers right now', list);
        });
        return;
      }
      var t = tries[i];
      fetch(VIEW + '&q=' + encodeURIComponent(t + '*'), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var list = (d.results || []).filter(function (p) { return hit(p, t); }).slice(0, 4);
          if (!list.length) return attempt(i + 1);
          paint('Nothing matched \u201c' + esc(q) + '\u201d exactly',
            'Showing the closest we have for \u201c' + esc(t) + '\u201d', list);
        })
        .catch(function () { attempt(i + 1); });
    }

    attempt(0);
  }

  function scan() {
    injectCss();
    Array.prototype.forEach.call(document.querySelectorAll('input[name="q"]'), attach);
    pageFallback();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  /* The theme opens its search drawer lazily, so inputs can appear after load. Watch for
     them — but coalesce to one scan per frame: this store's header mutates constantly
     (carousels, cart drawer, breeze scripts) and an unthrottled observer would run a
     document-wide querySelectorAll on every one of those mutations. */
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; scan(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
